import React, { useRef, useState, useEffect } from 'react';
import BubbleCommon from '@op/components/BubbleCommon';

interface EllipsisTextProps {
    text: string;
    className?: string;
    style?: React.CSSProperties;
    bubbleProps?: any; // To pass extra props to BubbleCommon if needed
}

/**
 * A component that displays text with ellipsis when it overflows,
 * and shows a BubbleCommon tooltip with the full text on hover.
 */
const EllipsisText: React.FC<EllipsisTextProps> = ({
    text,
    className,
    style,
    bubbleProps
}) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const element = textRef.current;
        if (!element) return;

        const checkOverflow = () => {
            // Check if text is overflowing
            // Use logic: scrollWidth > clientWidth
            const isTruncated = element.scrollWidth > element.clientWidth;
            setIsOverflowing(isTruncated);
        };

        // Initial check
        checkOverflow();

        // Re-check on window resize
        const handleResize = () => checkOverflow();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [text]);

    const TextElement = (
        <span
            ref={textRef}
            className={className}
            style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...style
            }}
        >
            {text}
        </span>
    );

    if (isOverflowing) {
        return (
            <BubbleCommon
                tipsMap={null}
                content={text}
                dark={true}
                showIcon={false}
                childStyle={{
                    display: 'block',
                    overflow: 'hidden',
                    minWidth: 0,
                }}
                {...bubbleProps}
            >
                {TextElement}
            </BubbleCommon>
        );
    }

    return TextElement;
};

export default EllipsisText;
